import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ErrorReportView } from "./ErrorReportView";
import { ErrorDetailsView } from "./ErrorDetailsView";
import { useTerminalAnimation } from "@/hooks/use-terminal-animation";
import { cn } from "@/lib/utils";
import { APP_VERSION } from "@/lib/constants";
import type { ErrorInfo } from "react";

type ViewState = "main" | "details" | "report" | "submitted";

interface ErrorScreenProps {
  error: Error;
  errorInfo?: ErrorInfo | null | undefined;
  statusCode?: number | undefined;
  onReset?: (() => void) | undefined;
}

export function ErrorScreen({ error, errorInfo, statusCode, onReset }: ErrorScreenProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState<ViewState>("main");

  const baseMessage = error.message || "Unable to reach the CompBox service";
  const errorMessage = statusCode ? `[${statusCode}] ${baseMessage}` : baseMessage;
  const route = location.pathname;
  const stackTrace = errorInfo?.componentStack ?? error.stack ?? "";

  const anim = useTerminalAnimation();
  const L = anim.visibleLines; // shorthand

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  const handleGoBack = useCallback(() => {
    onReset?.();
    void navigate("/dashboard");
  }, [navigate, onReset]);

  const handleDetails = useCallback(() => { setView("details"); }, []);
  const handleReport = useCallback(() => { setView("report"); }, []);

  // Keyboard shortcuts: 1-4 to select options
  useEffect(() => {
    if (view !== "main" || !anim.allDone) return;
    const handler = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input/textarea
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      switch (e.key) {
        case "1": e.preventDefault(); handleRetry(); break;
        case "2": e.preventDefault(); handleGoBack(); break;
        case "3": e.preventDefault(); handleDetails(); break;
        case "4": e.preventDefault(); handleReport(); break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); };
  }, [view, anim.allDone, handleRetry, handleGoBack, handleDetails, handleReport]);

  if (view === "details") {
    return (
      <ErrorDetailsView
        error={error}
        errorInfo={errorInfo}
        route={route}
        onRetry={handleRetry}
        onReport={() => { setView("report"); }}
        onBack={() => { setView("main"); }}
      />
    );
  }

  if (view === "report" || view === "submitted") {
    return (
      <ErrorReportView
        error={error}
        route={route}
        stackTrace={stackTrace}
        isSubmitted={view === "submitted"}
        onSubmit={() => { setView("submitted"); }}
        onRetry={handleRetry}
        onBack={() => { setView("main"); }}
        onViewDetails={() => { setView("details"); }}
      />
    );
  }

  const errorDescription = error.name === "TypeError"
    ? "A type error occurred while rendering this page."
    : "Connection timed out after 30s. Check your network or try again.";

  // Lines map:
  // L>=1: spacer + error header
  // L>=2: description
  // L>=3: divider
  // L>=4: "What would you like to do?"
  // L>=5: option 1
  // L>=6: option 2
  // L>=7: option 3
  // L>=8: option 4
  // L>=9: spacer
  // L>=10: prompt line
  // L>=11: version footer

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center justify-center transition-colors duration-500 ease-out",
        anim.showBackdrop ? "bg-black" : "bg-black/0"
      )}
      onClick={anim.skipAnimation}
      role="alert"
      aria-live="assertive"
    >
      {/* Terminal window */}
      <div
        className={cn(
          "w-full max-w-[700px] px-4 md:px-0",
          anim.showTerminal ? "animate-terminal-entrance" : "opacity-0 translate-y-[60px]"
        )}
        onClick={(e) => { e.stopPropagation(); }}
        role="presentation"
      >
        <div className="rounded-xl border border-[#3a3a3a] bg-[#1c1c1c] shadow-[0_16px_60px_rgba(0,0,0,0.7),0_0_120px_10px_rgba(51,230,102,0.06)] overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center h-12 px-5 bg-[#252525]">
            <div className="flex gap-2">
              <div className="w-[13px] h-[13px] rounded-full bg-[#ff5f57]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#febc2e]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 text-center">
              <span className="text-[13px] text-[#777] font-mono font-medium">
                compbox — error
              </span>
            </div>
            <div className="w-[54px]" />
          </div>

          <div className="h-px bg-[#333]" />

          {/* Terminal body */}
          <div className="bg-[#111] p-4 md:p-7 font-mono">
            {/* Command line — typed char by char */}
            {anim.typedCommand.length > 0 && (
              <div className="flex items-center flex-wrap">
                <span className="text-[14px] whitespace-pre">{renderTypedCommand(anim.typedCommand)}</span>
                {!anim.commandDone && (
                  <span className="text-green-400 animate-terminal-blink">▊</span>
                )}
              </div>
            )}

            {/* Line 1: Error header */}
            {L >= 1 && (
              <div className="animate-line-reveal">
                <div className="h-5" />
                <div>
                  <span className="text-[15px] text-amber-400">⚠ </span>
                  <span className="text-[14px] font-bold text-red-400">Error: </span>
                  <span className="text-[14px] text-[#e5e5e5]">{errorMessage}</span>
                </div>
              </div>
            )}

            {/* Line 2: Description */}
            {L >= 2 && (
              <div className="animate-line-reveal">
                <p className="text-[13px] text-[#9ca3af] mt-1.5">{errorDescription}</p>
              </div>
            )}

            {/* Line 3: Divider */}
            {L >= 3 && (
              <div className="animate-line-reveal">
                <div className="h-6" />
                <div className="h-px bg-[#2a2a2a] w-full" />
                <div className="h-6" />
              </div>
            )}

            {/* Line 4: Prompt */}
            {L >= 4 && (
              <div className="animate-line-reveal">
                <p className="text-[14px] font-medium text-[#d1d5db] mb-4">
                  What would you like to do?
                </p>
              </div>
            )}

            {/* Lines 5-8: Options */}
            <div className="space-y-1">
              {L >= 5 && <div className="animate-line-reveal"><TerminalOption number="1" color="text-green-400" icon="↻" label="Retry connection" onClick={handleRetry} /></div>}
              {L >= 6 && <div className="animate-line-reveal"><TerminalOption number="2" color="text-[#deb94a]" icon="←" label="Go back to dashboard" onClick={handleGoBack} /></div>}
              {L >= 7 && <div className="animate-line-reveal"><TerminalOption number="3" color="text-cyan-400" icon="⌥" label="View error details" onClick={handleDetails} /></div>}
              {L >= 8 && <div className="animate-line-reveal"><TerminalOption number="4" color="text-violet-400" icon="⚑" label="Report this issue" onClick={handleReport} /></div>}
            </div>

            {/* Line 9-10: Footer prompt */}
            {L >= 10 && (
              <div className="animate-line-reveal">
                <div className="h-7" />
                <div className="flex items-center text-[13px]">
                  <span className="text-[#9ca3b0]">Press a number to continue </span>
                  <span className="text-green-400 animate-terminal-blink">▊</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Version footer */}
      {L >= 11 && (
        <div className="mt-8 text-[12px] text-[#4b5563] font-sans animate-line-reveal">
          compbox {APP_VERSION}
        </div>
      )}
    </div>
  );
}

/** Render the typed command with proper color coding */
function renderTypedCommand(text: string) {
  const parts: { text: string; className: string }[] = [
    { text: "$ ", className: "text-green-400 font-bold" },
    { text: "compbox run ", className: "text-[#e5e5e5] font-medium" },
    { text: "--check", className: "text-[#6b7280]" },
  ];

  let remaining = text.length;
  return parts.map((part, i) => {
    if (remaining <= 0) return null;
    const chars = Math.min(remaining, part.text.length);
    remaining -= chars;
    return (
      <span key={i} className={part.className}>
        {part.text.slice(0, chars)}
      </span>
    );
  });
}

function TerminalOption({
  number,
  color,
  icon,
  label,
  onClick,
}: {
  number: string;
  color: string;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 rounded-md px-3 py-2 hover:bg-white/5 transition-colors w-full text-left cursor-pointer"
    >
      <span className="flex items-center font-mono text-[14px]">
        <span className="text-[#555]">[</span>
        <span className={cn("font-bold", color)}>{number}</span>
        <span className="text-[#555]">]</span>
      </span>
      <span className="flex items-center gap-1 font-mono text-[14px]">
        <span className="text-[#888]">{icon}  </span>
        <span className="text-[#e5e5e5]">{label}</span>
      </span>
    </button>
  );
}
